/**
 * Single source of truth for SEO indexability rules.
 * Used by every SEO route to decide robots meta + canonical strategy.
 */

export type IndexabilityInput = {
  providerCount: number;
  seoScore?: number;
  hasFaq?: boolean;
  hasNearbyCities?: boolean;
  internalLinkCount?: number;
  hasRelatedProviders?: boolean;
  missingCoreFields?: boolean;
  isDuplicate?: boolean;
};

export type IndexabilityResult = {
  index: boolean;
  follow: boolean;
  robots: string;
  reason: string;
};

export function decideIndexability(input: IndexabilityInput): IndexabilityResult {
  const {
    providerCount,
    seoScore = 100,
    hasFaq = false,
    hasNearbyCities = false,
    internalLinkCount = 0,
    hasRelatedProviders = false,
    missingCoreFields = false,
    isDuplicate = false,
  } = input;

  if (missingCoreFields) {
    return { index: false, follow: true, robots: "noindex, follow", reason: "missing_core_fields" };
  }
  if (isDuplicate) {
    return { index: false, follow: true, robots: "noindex, follow", reason: "duplicate" };
  }
  if (providerCount === 0) {
    return { index: false, follow: true, robots: "noindex, follow", reason: "zero_providers" };
  }
  if (seoScore < 40) {
    return { index: false, follow: true, robots: "noindex, follow", reason: "low_seo_score" };
  }
  if (providerCount <= 2) {
    const enriched = hasFaq && hasNearbyCities && hasRelatedProviders && internalLinkCount >= 5;
    if (!enriched) {
      return { index: false, follow: true, robots: "noindex, follow", reason: "thin_page" };
    }
  }
  return { index: true, follow: true, robots: "index, follow", reason: "ok" };
}

export function robotsMeta(robots: string) {
  return { name: "robots", content: robots };
}
